import { unref, watch } from "vue";
import { useCustomPriceFees } from "@/composables/authorization.js";
import { APPLICATION_FEE_DATA, CONFIRMATION_PAYMENT_PRICE_IN_CENTS } from "@/constants";
import { dollarsToCents } from "@/mixins/helpers";

/**
 * A session that has never been persisted has neither an id nor a created at date. Only those
 * get the CMS defaults: on an existing session both fee fields arrive `undefined` whenever the
 * columns are NULL, which is how a saved $0 is stored, and defaulting there would overwrite it.
 *
 * @param {Object|undefined} session
 * @returns {boolean}
 */
const sessionIsNew = (session) => !session?.id && !session?.created_at;

/**
 * Applies CMS defaults for configurable Application Fee / Confirmation Deposit.
 *
 * Caller must pass the live session model. This composable mutates the session
 * only for a new session whose fee fields are both `undefined`.
 *
 * `null`, `0`, or a number are treated as explicit API values and are preserved.
 *
 * @param {import("vue").Ref|import("vue").ComputedRef|Object} programSessionRef
 */
export const useConfigurableSessionPayments = (programSessionRef) => {
  watch(
    () => unref(programSessionRef)?.id,
    () => {
      if (!unref(useCustomPriceFees)) {
        return;
      }

      const session = unref(programSessionRef);

      if (!session) {
        return;
      }

      if (!sessionIsNew(session)) {
        return;
      }

      if (session.application_fee_in_cents !== undefined || session.confirmation_deposit_in_cents !== undefined) {
        return;
      }

      session.application_fee_in_cents = dollarsToCents(APPLICATION_FEE_DATA.amount);
      session.confirmation_deposit_in_cents = CONFIRMATION_PAYMENT_PRICE_IN_CENTS;
    },
    { immediate: true },
  );
};
